import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { ActivityCategory, type User } from "../types";
import { PASSIONS } from "../constants";
import {
  ArrowLeftIcon,
  CheckIcon,
  CameraIcon,
  XIcon,
} from "../components/icons/FeatherIcons";
import { AnimatePresence, motion } from "framer-motion";
import PhotoSelectionModal from "../components/PhotoSelectionModal";
import {
  generatePersonaTitle,
  generateAlternativePersonas,
} from "../utils/personaUtils";
import { usePageTracking } from "../hooks/usePageTracking";
import { createPortal } from "react-dom";
import { getNames } from "country-list";
import { useCachedImage } from "../hooks/useCachedImage";
import { Capacitor } from "@capacitor/core";
import { DatetimePicker } from "@capawesome-team/capacitor-datetime-picker";
import { fallbackImage } from "../utils/getCachedImage";

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const countries: string[] = getNames(); // returns array of country names

// Helper component for displaying cached profile image
const ProfileImageDisplay: React.FC<{ formData: Partial<User> }> = ({
  formData,
}) => {
  const cachedImage = useCachedImage(
    formData.profileImageUrl,
    formData.username || "user",
    formData.id ? String(formData.id) : "unknown",
  );
  return (
    <img
      src={cachedImage}
      alt="Profile"
      className="w-full h-full object-cover"
      onError={handleImageError}
    />
  );
};

const EditProfilePage: React.FC = () => {
  usePageTracking("EditProfile", "EditProfilePage");

  const isNative = Capacitor.isNativePlatform();

  const { currentUser, updateUser } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<Partial<User>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPassionModalOpen, setIsPassionModalOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [recommendedPersona, setRecommendedPersona] = useState<string>("");
  const [showPersonaOptions, setShowPersonaOptions] = useState(false);
  const [usernameError, setUsernameError] = useState<string>("");

  const handleDateClick = async () => {
    if (isNative) {
      try {
        const result = await DatetimePicker.present({
          // Set the mode (date, time, or datetime)
          mode: "date",
          androidDatePickerMode: "spinner",
          // The format property defines how the selected date is displayed to the user
          format: "yyyy-MM-dd",
          // The 'value' must adhere to the ISO 8601 format (YYYY-MM-DDTHH:mmZ) internally
          value: formData.dateOfBirth || new Date().toISOString().split("T")[0],
          // You can also set locales to match regional preferences
          locale: "en-SG",
        });
        if (result.value) {
          setFormData({ ...formData, dateOfBirth: result.value.split("T")[0] });
        }
      } catch (e) {
        // handle cancel or error
      }
    }
  };

  useEffect(() => {
    if (currentUser) {
      setFormData(currentUser);
      // Initialize recommended persona based on current passions
      if (currentUser.passions && currentUser.passions.length > 0) {
        const initialRecommendedPersona = generatePersonaTitle(
          currentUser.passions,
        );
        // Only show recommendation if it's different from current persona title
        if (initialRecommendedPersona !== currentUser.personaTitle) {
          setRecommendedPersona(initialRecommendedPersona);
        }
      }
    }
  }, [currentUser]);

  const handleSave = async () => {
    if (!currentUser) return;
    setIsLoading(true);
    setUsernameError(""); // Clear any previous errors
    try {
      await updateUser({ ...formData, id: currentUser.id });
      addToast("Profile updated successfully!", "success");
      navigate(-1);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update profile.";

      // Check if it's a username error
      if (errorMessage.includes("username") && errorMessage.includes("taken")) {
        setUsernameError(errorMessage);
      }

      addToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoSelect = (url: string) => {
    setFormData((prev) => ({
      ...prev,
      profileImageUrl: url,
    }));
  };

  const handlePassionSelect = (passion: string) => {
    setFormData((prev) => {
      const currentPassions = prev.passions || [];
      let newPassions;

      if (currentPassions.includes(passion)) {
        newPassions = currentPassions.filter((p) => p !== passion);
      } else if (currentPassions.length >= 7) {
        return prev;
      } else {
        newPassions = [...currentPassions, passion];
      }

      // Generate recommended persona title based on updated passions
      const newRecommendedPersona = generatePersonaTitle(newPassions);
      // Only show recommendation if it's different from current persona title
      if (newRecommendedPersona !== prev.personaTitle) {
        setRecommendedPersona(newRecommendedPersona);
      } else {
        setRecommendedPersona("");
      }

      // Clear persona options when passions change
      setShowPersonaOptions(false);

      return { ...prev, passions: newPassions };
    });
  };

  if (!currentUser) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Responsive container: mobile-first with larger breakpoints */}
      <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto h-full flex flex-col">
        {/* Header */}
        <header className="flex-shrink-0 bg-white px-3 md:px-6 pb-3 pt-3 flex items-center justify-between shadow-sm border-b border-gray-200 sticky-header">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-lg md:text-xl font-bold text-gray-900">
            Edit Profile
          </h1>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="font-bold text-primary disabled:text-gray-400 text-sm md:text-base"
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </header>

        {/* Form */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 pb-24">
          <div className="space-y-6 max-w-xl mx-auto">
            {/* Profile Picture Section */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                Profile Picture
              </h2>
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex flex-col items-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden mb-4 bg-gray-100 border-2 border-gray-200">
                    {/* Use cached profile image */}
                    <ProfileImageDisplay formData={formData} />
                  </div>
                  <button
                    onClick={() => setIsPhotoModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <CameraIcon className="w-5 h-5" />
                    <span>Change Profile Picture</span>
                  </button>
                </div>
              </div>
            </div>
            {/* Info Section */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                About You
              </h2>
              <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
                <div className="p-3">
                  <label className="text-sm text-gray-500">Username</label>
                  <p className="text-xs text-gray-400 mb-1">
                    Your unique identifier on the platform
                  </p>
                  <input
                    type="text"
                    value={formData.username || ""}
                    onChange={(e) => {
                      setFormData({ ...formData, username: e.target.value });
                      setUsernameError(""); // Clear error when user types
                    }}
                    className={`w-full text-base text-gray-900 focus:outline-none bg-transparent ${usernameError ? "border-b-2 border-red-500" : ""}`}
                  />
                  {usernameError && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      ⚠️ {usernameError}
                    </p>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm text-gray-500">
                      Persona Title
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          const alternatives = generateAlternativePersonas(
                            formData.passions || [],
                            formData.personaTitle,
                          );
                          if (alternatives.length > 0) {
                            setRecommendedPersona(alternatives[0]);
                            setShowPersonaOptions(true);
                          } else {
                            // Generate a fresh recommendation if no alternatives
                            const newRecommendedPersona = generatePersonaTitle(
                              formData.passions || [],
                            );
                            setRecommendedPersona(newRecommendedPersona);
                            setShowPersonaOptions(false);
                          }
                        }}
                        className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full hover:bg-blue-200 transition-colors font-medium"
                      >
                        💡 Recommend
                      </button>
                      {recommendedPersona &&
                        recommendedPersona !== formData.personaTitle &&
                        !showPersonaOptions && (
                          <button
                            onClick={() => {
                              setFormData({
                                ...formData,
                                personaTitle: recommendedPersona,
                              });
                              setRecommendedPersona("");
                            }}
                            className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full hover:bg-purple-200 transition-colors"
                          >
                            Use "{recommendedPersona}"
                          </button>
                        )}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={formData.personaTitle || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setFormData({ ...formData, personaTitle: newValue });
                      // Clear recommendations if user manually changes persona
                      if (newValue !== recommendedPersona) {
                        setRecommendedPersona("");
                        setShowPersonaOptions(false);
                      }
                    }}
                    className="w-full text-base text-gray-900 focus:outline-none bg-transparent"
                    placeholder="e.g., the Adventurer"
                  />

                  {/* Persona Recommendations */}
                  {showPersonaOptions && (
                    <div className="mt-3 p-3 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        💡 Persona suggestions based on your interests:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {generateAlternativePersonas(
                          formData.passions || [],
                          formData.personaTitle,
                        ).map((persona, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                personaTitle: persona,
                              });
                              setShowPersonaOptions(false);
                              setRecommendedPersona("");
                            }}
                            className="text-xs bg-white border border-purple-200 text-purple-700 px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors font-medium"
                          >
                            {persona}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setShowPersonaOptions(false)}
                        className="text-xs text-gray-500 mt-2 hover:text-gray-700"
                      >
                        ✕ Close suggestions
                      </button>
                    </div>
                  )}

                  {recommendedPersona &&
                    recommendedPersona !== formData.personaTitle &&
                    !showPersonaOptions && (
                      <p className="text-xs text-purple-600 mt-1">
                        💡 Based on your interests, we suggest: "
                        {recommendedPersona}"
                      </p>
                    )}
                </div>
                <div className="p-3">
                  <label className="text-sm text-gray-500">Bio</label>
                  <textarea
                    value={formData.bio || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, bio: e.target.value })
                    }
                    className="w-full text-base text-gray-900 focus:outline-none bg-transparent"
                    rows={3}
                    placeholder="Tell us about yourself..."
                  />
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div>
              <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
                <div className="p-3">
                  <label className="text-sm text-gray-500">Date of Birth</label>
                  {isNative ? (
                    <input
                      type="text"
                      value={formData.dateOfBirth || ""}
                      readOnly
                      onClick={handleDateClick}
                      placeholder="Select date"
                      className="w-full text-base text-gray-900 focus:outline-none bg-transparent cursor-pointer"
                    />
                  ) : (
                    <input
                      type="date"
                      value={formData.dateOfBirth || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dateOfBirth: e.target.value,
                        })
                      }
                      className="w-full text-base text-gray-900 focus:outline-none bg-transparent block
                                                    [&::-webkit-date-and-time-value]:text-left
                                                    [&::-webkit-date-and-time-value]:m-0"
                    />
                  )}
                </div>
                <div className="p-3">
                  <label className="text-sm text-gray-500">Location</label>
                  <select
                    value={formData.location || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full text-base text-gray-900 focus:outline-none bg-transparent"
                  >
                    <option value="">Select a country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="p-3">
                  <label className="text-sm text-gray-500 mb-2 block">
                    Gender
                  </label>
                  <div className="flex space-x-2">
                    {(["male", "female", "other", "undisclosed"] as const).map(
                      (gender) => (
                        <button
                          key={gender}
                          type="button"
                          onClick={() => setFormData({ ...formData, gender })}
                          className={`flex-1 p-2 text-sm rounded-lg border-2 font-semibold transition-colors ${formData.gender === gender ? "bg-primary/10 border-primary text-primary" : "bg-gray-100 border-transparent text-gray-700"}`}
                        >
                          {gender.charAt(0).toUpperCase() + gender.slice(1)}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Passions Section */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                Passions
              </h2>
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {(formData.passions || []).map((passion) => (
                    <div
                      key={passion}
                      className="px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-sm font-semibold"
                    >
                      {passion}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setIsPassionModalOpen(true)}
                  className="w-full mt-3 text-center p-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200"
                >
                  Edit Passions
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Photo Selection Modal */}
        <Portal>
          {isPhotoModalOpen && (
            <PhotoSelectionModal
              isOpen={isPhotoModalOpen}
              onClose={() => setIsPhotoModalOpen(false)}
              onSelect={(url) => {
                handlePhotoSelect(url);
                setIsPhotoModalOpen(false);
              }}
              currentPhoto={formData.profileImageUrl}
            />
          )}
        </Portal>

        {/* Passion Modal */}
        <Portal>
          <AnimatePresence>
            {isPassionModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsPassionModalOpen(false)}
                className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col p-4"
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Select Your Passions
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">Choose up to 7.</p>
                  <div className="flex-1 overflow-y-auto flex flex-wrap gap-2 content-start">
                    {PASSIONS.map((passion) => {
                      const isSelected = formData.passions?.includes(passion);
                      return (
                        <button
                          key={passion}
                          type="button"
                          onClick={() => handlePassionSelect(passion)}
                          className={`px-3 py-1.5 rounded-full border-2 font-semibold transition-all duration-200 text-sm ${isSelected ? "bg-primary/10 border-primary text-primary" : "bg-gray-100 border-transparent text-gray-700 hover:border-gray-300"}`}
                        >
                          {passion}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setIsPassionModalOpen(false)}
                    className="w-full mt-4 bg-primary text-white font-bold py-3 rounded-full"
                  >
                    Done
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </Portal>
      </div>
      {/* End of responsive container */}
    </div>
  );
};

export default EditProfilePage;
